/* eslint-disable react/jsx-no-duplicate-props */
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { FilledInputProps } from '@material-ui/core/FilledInput';
import { FixedSizeList as List } from 'react-window';
import styled, { css } from 'styled-components';
import { PopperPlacementType } from '@material-ui/core/Popper';
import { SystemIcon } from '@utmost.co/utmost-design-system';
import { Keys } from '../../../forms/utils/keysUtil';
import { disabledAutoComplete, useAnonymousName } from '../../utils/disableBrowserAutocomplete';
import { FieldValue } from '../../../types';
import { InfoText } from '../../InfoText/InfoText';
import { TypeaheadOption } from './types';
import { TypeaheadDropdown } from './TypeaheadDropdown';

const MENU_ITEM_LARGE = 56;
const MENU_ITEM_SMALL = 46;

export interface Props<OptionValue extends any = FieldValue> {
  name: string;
  label: string;
  className?: string;
  sort?: boolean;
  error?: boolean;
  helpText?: string;
  infoText?: string;
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  icon?: JSX.Element;
  loading?: boolean;
  /** The text to render in the input box */
  textValue: string;
  /** Describes which of the provided options are selected in the dropdown */
  selected?: TypeaheadOption<OptionValue>[];
  /** The options to pick from the drop down  */
  options: TypeaheadOption<OptionValue>[];
  fullWidth?: boolean;
  required?: boolean;
  filter?: boolean;
  itemSize: 'small' | 'large';
  inputStyle?: CSSProperties;
  dropdownIconStyle?: CSSProperties;
  InputProps?: Partial<FilledInputProps>;
  closeOnSelect?: boolean;
  placement?: PopperPlacementType;
  setShowAllMenuItems: (showAll: boolean) => void;
  onBlur?: () => void;
  onMenuClose?: () => void;
  setValues: (selectedValues?: TypeaheadOption<OptionValue>[]) => void;
  /** Called whenever the input text changes */
  onFilterChange?: (value: string) => void;
  onSetInputText: (val: string) => void;
  onDropDownClick?: () => void;
  onTextFieldClicked?: () => void;
  dataTest?: string;
  clearable?: boolean;
  /** Whether infinite scrolling is active (feature-flagged and API-driven). */
  infiniteScroll?: boolean;
  /** Called when the user scrolls near the end of the options list. */
  onLoadMore?: () => void;
  /** Whether another page of options is available to load. */
  hasNextPage?: boolean;
  /** Whether a `onLoadMore` request is currently in flight. */
  loadingMore?: boolean;
}

export function SelectComponent<OptionValue extends any = FieldValue>(props: Props<OptionValue>) {
  const {
    name,
    label,
    className,
    sort,
    error,
    helpText,
    infoText,
    placeholder,
    disabled,
    multiple,
    icon,
    loading,
    textValue,
    selected,
    options,
    fullWidth,
    itemSize,
    required,
    InputProps,
    dropdownIconStyle,
    filter,
    inputStyle,
    closeOnSelect,
    placement,
    dataTest,
    onBlur,
    onMenuClose,
    setValues,
    setShowAllMenuItems,
    onFilterChange,
    onSetInputText,
    onDropDownClick,
    onTextFieldClicked,
    clearable = true,
    infiniteScroll,
    onLoadMore,
    hasNextPage,
    loadingMore
  } = props;
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [isFocussed, setIsFocussed] = useState(false);
  const [focusedMenuItem, setFocusedMenuItem] = useState<number | null>(null);
  const [inputText, setInputText] = useState(textValue || '');

  const inputRef = useRef<HTMLInputElement>();
  const isInputFocused = document.activeElement === inputRef.current;

  const menuRef = useRef<HTMLDivElement | null>(null);
  const listRef = React.createRef<List>();

  const componentName = useAnonymousName(name ?? '');

  useEffect(() => {
    if (!isFocussed) {
      setInputText(textValue);
    }
  }, [textValue, isFocussed]);

  const handleSetInputText = useCallback(
    (val: string) => {
      setInputText(val);
      onSetInputText(val);
    },
    [onSetInputText]
  );

  const handleSetValue = useCallback(
    (selectedValues: TypeaheadOption<OptionValue>[] | undefined, focusOnInput?: boolean) => {
      if (!multiple) {
        setFocusedMenuItem(null);
        setIsOpenDropdown(false);
        const option = selectedValues?.[0];
        const label = option?.label ? `${option?.label}${option?.code ? ` ${option?.code}` : ''}` : '';
        handleSetInputText(label);
        setValues(selectedValues);
      } else setValues(selectedValues);

      if (multiple && closeOnSelect) {
        setIsOpenDropdown(false);
        setFocusedMenuItem(null);
        inputRef.current?.focus();
      }

      if (focusOnInput) {
        inputRef.current?.focus();
      }
    },
    [handleSetInputText, multiple, closeOnSelect, setValues]
  );

  const closeDropdown = useCallback(
    (event?: { target: any }) => {
      // Check if isOpenDropdown is already closed as this handler can
      // be called multiple times is quick succession which can raise
      // unwanted `act` warnings during testing.
      if (!isOpenDropdown || menuRef.current?.contains(event?.target as Node)) {
        return;
      }
      onMenuClose?.();
      setIsOpenDropdown(false);
      if (!multiple) {
        handleSetInputText(textValue ?? '');
      }
    },
    [isOpenDropdown, onMenuClose, multiple, textValue, handleSetInputText]
  );

  const blurOnInput = useCallback(
    (e: { relatedTarget: EventTarget | null }) => {
      if (!e.relatedTarget) {
        setIsFocussed(false);
        if (onBlur) onBlur();
      } else {
        if (!textValue && onFilterChange) {
          // Selecting in a multi-select blurs the input (focus moves to the menu item) while
          // the dropdown stays open. Only reset the filter when there is an actual search
          // term to clear — a plain pick must NOT trigger a needless refetch, which would
          // flip the loader and reset the list scroll position. When we do clear, also clear
          // the visible search text so a stale term doesn't linger over the unfiltered list.
          if (inputText) {
            onFilterChange('');
            handleSetInputText('');
          }
        }
      }
    },
    [onBlur, onFilterChange, textValue, inputText, handleSetInputText]
  );

  const keyDownOnInput = useCallback(
    (e: React.KeyboardEvent) => {
      setShowAllMenuItems(false);
      if (Keys.isOneOf(e, ['enter'])) {
        if (!isOpenDropdown) {
          setIsOpenDropdown(true);
          setFocusedMenuItem(0);
        } else if (!options[0]?.disabled) {
          const isOptionSelected = selected?.some((selectedItem) => options[0]!.id === selectedItem.id);

          if (!isOptionSelected && multiple) {
            handleSetValue(selected ? selected.concat(options[0]!) : [options[0]!]);
          } else {
            handleSetValue(options[0] ? [options[0]] : undefined);

            (document.activeElement as HTMLElement)?.blur();
            e.stopPropagation();
            e.preventDefault();
          }
        }
      }
      if (Keys.isOneOf(e, ['spacebar']) && !inputText) {
        e.stopPropagation();
        e.preventDefault();
        setIsOpenDropdown(true);
      }
      if (Keys.isOneOf(e, ['tab', 'escape'])) {
        if (isOpenDropdown) {
          e.stopPropagation();
          e.preventDefault();
          closeDropdown();
        }
      }
      if (Keys.isOneOf(e, ['arrowUp', 'arrowDown'])) {
        const min = required ? 1 : 2;
        if (options.length >= min && listRef.current) {
          listRef.current.scrollToItem(1);
          setFocusedMenuItem(1);
          menuRef.current?.focus();
        }
      }
    },
    [
      closeDropdown,
      inputText,
      multiple,
      isOpenDropdown,
      listRef,
      required,
      handleSetValue,
      options,
      setShowAllMenuItems,
      selected
    ]
  );

  const keyDownOnMenuWrapper = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (Keys.isOneOf(e, ['arrowUp', 'arrowDown'])) {
        const isDown = Keys.is(e, 'arrowDown');
        let elNo = focusedMenuItem !== null ? focusedMenuItem + (isDown ? 1 : -1) : 0;

        if (elNo < 0) {
          elNo = options ? options.length - 1 : 0;
        } else if (elNo > options!.length - 1) {
          elNo = 0;
        }

        if (listRef.current) {
          listRef.current.scrollToItem(elNo);
          setFocusedMenuItem(elNo);
        }
        if (menuRef.current) {
          menuRef.current.focus();
        }
      }
      if (Keys.isOneOf(e, ['enter']) && focusedMenuItem !== null) {
        const item = options?.[focusedMenuItem];

        if (item && !item.disabled) {
          const isOptionSelected = selected?.some((selectedItem) => item.id === selectedItem.id);

          if (!isOptionSelected && multiple) {
            handleSetValue(selected ? selected.concat(item) : [item], true);
          } else {
            handleSetValue([item], true);

            (document.activeElement as HTMLElement)?.blur();
            e.stopPropagation();
            e.preventDefault();
          }
        }
      }
      if (Keys.isOneOf(e, ['tab', 'escape'])) {
        e.stopPropagation();
        e.preventDefault();
        closeDropdown();
        inputRef.current?.focus();
      }
    },
    [closeDropdown, focusedMenuItem, listRef, multiple, handleSetValue, options, selected]
  );

  const clickOnInput = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as any)?.select && filter) {
        (e.target as HTMLInputElement).select();
      }
      onTextFieldClicked?.();
      setIsOpenDropdown(true && !disabled);
      setFocusedMenuItem(0);
    },
    [disabled, filter, onTextFieldClicked]
  );

  const clickOnIcon = useCallback(() => {
    if (onDropDownClick) onDropDownClick();
    setShowAllMenuItems(true);
    setIsOpenDropdown(true);
    setFocusedMenuItem(0);
  }, [setShowAllMenuItems, onDropDownClick]);

  const changeOnTextField = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      if (!e.target.value) {
        setShowAllMenuItems(true);
      }
      setIsOpenDropdown(true);
      setFocusedMenuItem(0);
      handleSetInputText(e.target.value);
      onFilterChange?.(e.target.value);
    },
    [onFilterChange, setShowAllMenuItems, handleSetInputText]
  );

  const handleMouseDown = useCallback(() => {
    handleSetValue(undefined);
    if (onFilterChange) {
      handleSetInputText('');
      onFilterChange('');
    }
  }, [handleSetValue, handleSetInputText, onFilterChange]);

  const text = isInputFocused || isOpenDropdown ? (inputText ?? '') : (textValue ?? '');

  return (
    <ClickAwayListener onClickAway={closeDropdown}>
      <StyledTextfieldWrapper className={className} data-test={dataTest}>
        <TextField
          id={componentName}
          inputRef={inputRef}
          name={componentName}
          label={label && <InfoText label={label} required={required} tooltipText={infoText} />}
          type="text"
          onClick={clickOnInput}
          error={error}
          className="smartform-textfield"
          helperText={helpText}
          InputLabelProps={{
            shrink: true,
            required,
            // @ts-ignore: Provide better description why we use ts-ignore here
            'data-test': `${name}-field-label`
          }}
          placeholder={placeholder}
          InputProps={
            {
              'data-test': `${name}-field` as any,
              type: 'text',
              onFocus: () => setIsFocussed(true),
              startAdornment: icon && text && <InputAdornment position="start">{icon}</InputAdornment>,
              endAdornment: (
                <StyledEndAdornment $absolute={!!multiple}>
                  {loading && <CircularProgress className="loading" size={20} thickness={4} />}
                  {!disabled && clearable && text && isFocussed && (
                    <StyledSystemIcon
                      name="close"
                      size="small"
                      dataTest="clear-enum"
                      className="clear-btn"
                      onMouseDown={handleMouseDown}
                    />
                  )}
                  <DropDownIcon onClick={clickOnIcon} style={dropdownIconStyle} />
                </StyledEndAdornment>
              ),
              onBlur: blurOnInput,
              ...disabledAutoComplete,
              ...InputProps,
              style: { ...inputStyle, cursor: disabled ? 'default' : filter ? 'text' : 'pointer' },
              readOnly: !filter
            } as any
          }
          FormHelperTextProps={
            {
              'data-test': `${name}-field-helper`,
              id: `${name}-helper-text`
            } as any
          }
          inputProps={{
            className: `id_${props.name}`
          }}
          value={text}
          onKeyDown={keyDownOnInput}
          onChange={changeOnTextField}
          autoComplete={disabledAutoComplete.autoComplete}
          disabled={disabled}
          fullWidth={fullWidth}
        />
        {!disabled && (
          <TypeaheadDropdown
            isOpenDropdown={isOpenDropdown}
            focusedMenuItem={focusedMenuItem}
            selected={selected}
            menuRef={menuRef}
            listRef={listRef}
            anchorEl={inputRef.current?.parentElement}
            multiple={multiple}
            setValues={handleSetValue}
            placement={placement}
            items={options}
            sort={sort}
            itemSize={itemSize === 'large' ? MENU_ITEM_LARGE : MENU_ITEM_SMALL}
            keyDownOnMenuWrapper={keyDownOnMenuWrapper}
            infiniteScroll={infiniteScroll}
            onLoadMore={onLoadMore}
            hasNextPage={hasNextPage}
            loadingMore={loadingMore}
          />
        )}
      </StyledTextfieldWrapper>
    </ClickAwayListener>
  );
}

const StyledTextfieldWrapper = styled.div`
  display: contents;
`;

/*
 * When the multi-select wrap layout is on, the chips are the input's startAdornment and wrap
 * onto multiple rows, growing the input's height. Pin the trailing icons (loading/clear/dropdown)
 * to the vertical centre on the right so the dropdown arrow stays centre-right regardless of how
 * many chip rows there are, matching the MUI multi-select layout. Otherwise `display: contents`
 * makes this wrapper transparent to layout so the icons behave exactly as the previous fragment.
 */
const StyledEndAdornment = styled.div<{ $absolute?: boolean }>`
  ${({ $absolute }) =>
    $absolute
      ? css`
          display: flex;
          align-items: center;
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
        `
      : css`
          display: contents;
        `}
`;

const StyledSystemIcon = styled(SystemIcon)`
  cursor: pointer;
  &:hover svg {
    fill: initial;
  }
`;

const DropDownIcon: React.FC<
  React.PropsWithChildren<{
    onClick: (e: React.MouseEvent) => void;
    style?: CSSProperties;
  }>
> = ({ onClick, style }) => (
  <InputAdornment data-test="select-drop-icon" position="end" onClick={onClick} style={style}>
    <SystemIcon name="arrow_drop_down" style={{ fill: 'rgba(0, 0, 0, 0.54)' }} aria-label="Select Toggle" />
  </InputAdornment>
);
